import { z } from "zod";
import type { Database } from "./database.types";
import { STARTER_PHOTO_IDS } from "./starterPhotos";

/**
 * Every block type. `video`, `columns` and `column` come from migration
 * 20260929140807_video_and_columns_blocks, `section` from
 * 20260930081609_section_block, `button` from 20261007082616_button_block
 * and `carousel` from 20261007141347_carousel_block;
 * once they are applied and database.types.ts
 * regenerated, this is just the generated enum and the widening below (and
 * SiteBlockRow) can go.
 */
export type SiteBlockType =
  | Database["public"]["Enums"]["block_type"]
  | "video"
  | "columns"
  | "column"
  | "section"
  | "button"
  | "carousel";

/** A blocks row that may hold any SiteBlockType. */
export type SiteBlockRow = Omit<
  Database["public"]["Tables"]["blocks"]["Row"],
  "block_type"
> & { block_type: SiteBlockType };
export type SiteBlockInsert = Omit<
  Database["public"]["Tables"]["blocks"]["Insert"],
  "block_type"
> & { block_type: SiteBlockType };
export type SiteBlockUpdate = Omit<
  Database["public"]["Tables"]["blocks"]["Update"],
  "block_type"
> & { block_type?: SiteBlockType };

export const textBlockContentSchema = z
  .object({
    richText: z.json(),
  })
  .strict();

export const pageBlockContentSchema = z
  .object({
    // Row, Feature and Index styles; "card" and "link" are the original ids.
    display: z.enum(["card", "feature", "link"]).default("card"),
    showDescription: z.boolean().default(true),
  })
  .strict();

export const libraryBlockContentSchema = z
  .object({
    // "cards" is no longer offered; saved libraries show it as one column.
    layout: z.enum(["grid", "list", "index", "cards"]).default("grid"),
    order: z.enum(["manual", "newest", "oldest"]).default("manual"),
    name: z.string().max(100).default("Selected work"),
    mode: z.enum(["selected", "all"]).default("selected"),
    columns: z.number().int().min(1).max(6).default(3),
  })
  .strict();

/** A framed picture's or video's mat (imageBackgrounds.ts); none is the theme's panel. */
// A colour from elsewhere (an older menu offered section backgrounds, like
// Golden hour) reads as none rather than stopping the page from showing.
const mediaBackground = z
  .enum(["none", "cream", "sand", "blush", "sage", "ink", "white", "terracotta", "gold", "green", "forest", "sunrise", "meadow", "rose", "dusk"])
  .catch("none");
/** How wide a picture or video sits in the page column. */
const mediaSize = z.enum(["small", "medium", "full"]).default("full");
/** A width dragged to size, as a percentage of the column; it outranks `size`. */
const mediaWidth = z.number().int().min(20).max(100).optional();

export const pictureBlockContentSchema = z
  .object({
    background: mediaBackground,
    size: mediaSize,
    width: mediaWidth,
    // Standard, Framed or Full bleed; unset pictures follow pictureStyle().
    style: z.enum(["standard", "framed", "bleed"]).optional(),
    // Built-in art: the two illustrations, or a starter photograph
    // (starterPhotos.ts), shown until the person adds their own.
    sample: z.enum(["flora", "kindred", ...STARTER_PHOTO_IDS] as [string, ...string[]]).optional(),
    alt: z.string().max(500).default(""),
    caption: z.string().max(2_000).default(""),
    // The caption on the left, centred or on the right; unset follows the
    // section.
    align: z.enum(["left", "center", "right"]).optional(),
    fit: z.enum(["contain", "cover", "fill"]).default("cover"),
    crop: z.object({
      ratio: z.enum(["1:1", "4:5", "3:2", "16:9", "free"]),
      customRatio: z.number().min(0.001).max(1000).optional(),
      x: z.number().min(0).max(100).default(50),
      y: z.number().min(0).max(100).default(50),
      zoom: z.number().min(1).max(20.001).default(1),
    }).strict().optional(),
  })
  .strict();

export const quoteBlockContentSchema = z
  .object({
    text: z.string().trim().min(1).max(10_000),
    attribution: z.string().max(500).default(""),
  })
  .strict();

export const videoBlockContentSchema = z
  .object({
    // A YouTube or Vimeo link; empty until one is pasted.
    url: z.string().max(500).default(""),
    caption: z.string().max(2_000).default(""),
    // The caption's alignment, like pictures.
    align: z.enum(["left", "center", "right"]).optional(),
    // Standard, Framed or Full bleed, like pictures.
    style: z.enum(["standard", "framed", "bleed"]).default("standard"),
    // Width and frame colour, like pictures.
    size: mediaSize,
    width: mediaWidth,
    background: mediaBackground,
    // The provider's player, or a silent autoplaying loop without controls.
    playback: z.enum(["player", "loop"]).default("player"),
  })
  .strict();

export const columnsBlockContentSchema = z
  .object({
    // Even halves, or two thirds and one third either way round.
    style: z.enum(["even", "wide-left", "wide-right"]).default("even"),
    // Both sides' vertical alignment, saved before each side had its own;
    // read as each side's when it has none.
    valign: z.enum(["top", "center", "bottom"]).optional(),
  })
  .strict();

export const sectionBlockContentSchema = z
  .object({
    // A background from the site's palette (siteBackgrounds.ts); unknown
    // ids show as none.
    background: z.string().max(40).optional(),
    // Before backgrounds: Plain, Tinted or Contrast, read as a background.
    style: z.enum(["plain", "tinted", "contrast"]).optional(),
    // Style: a band across the page, or a panel inside the page column.
    width: z.enum(["full", "inset"]).default("full"),
    // Room above and below the section's content.
    spacing: z.enum(["compact", "regular", "roomy"]).default("regular"),
    // Text, captions and buttons on the left, centred or on the right;
    // blocks with an alignment of their own keep it.
    align: z.enum(["left", "center", "right"]).default("left"),
  })
  .strict();

/**
 * One call-to-action button: its words, where it goes (one of the site's
 * pages by its source id, a section on its own page, or a web or mailto
 * address) and its style.
 */
export const callToActionSchema = z
  .object({
    label: z.string().max(80).default("Get in touch"),
    page: z.string().max(64).optional(),
    // A section on this page (its source block id), scrolled to.
    section: z.string().max(64).optional(),
    url: z.string().max(500).optional(),
    // Solid in the accent, an outline, or a quiet link with an arrow.
    style: z.enum(["solid", "outline", "link"]).default("solid"),
    // A colour from the theme's button palette (buttonColours.ts); unset is
    // the accent, and an unknown one shows as the accent too.
    tone: z.string().max(40).optional(),
  })
  .strict();

/**
 * A row of images to step or swipe through: one at a time, or several in
 * view. Each is one of the site's images (an asset id) with a caption.
 */
export const carouselBlockContentSchema = z
  .object({
    items: z
      .array(
        z
          .object({
            assetId: z.string().max(64).optional(),
            // A starter photograph instead of an uploaded image.
            sample: z.enum(STARTER_PHOTO_IDS as [string, ...string[]]).optional(),
            caption: z.string().max(200).optional(),
          })
          .strict()
          .refine((item) => Boolean(item.assetId || item.sample), "An image or a starter photograph"),
      )
      .max(24)
      .default([]),
    layout: z.enum(["single", "strip"]).default("single"),
    // Every image cropped to one shape, so the row sits evenly.
    shape: z.enum(["landscape", "square", "portrait"]).default("landscape"),
    autoplay: z.boolean().default(false),
    caption: z.string().max(200).optional(),
  })
  .strict();

export const buttonBlockContentSchema = z
  .object({
    // One or two: a main action, and perhaps a second beside it.
    buttons: z.array(callToActionSchema).min(1).max(2).default([{ label: "Get in touch", style: "solid" }]),
    // Left, centre or right; unset follows the section.
    align: z.enum(["left", "center", "right"]).optional(),
  })
  .strict();

/** One side of a columns block; everything it holds is its children. */
export const columnBlockContentSchema = z
  .object({
    // Next to a taller side, its blocks sit at the top, centre or bottom.
    valign: z.enum(["top", "center", "bottom"]).default("top"),
  })
  .strict();

/**
 * A picture's style. Pictures saved before styles existed look as they did:
 * framed when they had a background behind a narrower image.
 */
export const pictureStyle = (content: BlockContent<"picture">) =>
  content.style ??
  (content.size !== "full" && content.background !== "none" ? "framed" : "standard");

export const blockContentSchemas = {
  text: textBlockContentSchema,
  page: pageBlockContentSchema,
  library: libraryBlockContentSchema,
  picture: pictureBlockContentSchema,
  quote: quoteBlockContentSchema,
  video: videoBlockContentSchema,
  columns: columnsBlockContentSchema,
  column: columnBlockContentSchema,
  section: sectionBlockContentSchema,
  button: buttonBlockContentSchema,
  carousel: carouselBlockContentSchema,
} satisfies Record<SiteBlockType, z.ZodType>;

export type BlockType = keyof typeof blockContentSchemas;
export type BlockContent<T extends BlockType> = z.infer<(typeof blockContentSchemas)[T]>;

export const parseBlockContent = <T extends BlockType>(blockType: T, content: unknown): BlockContent<T> =>
  blockContentSchemas[blockType].parse(content) as BlockContent<T>;
